if (!customElements.get('comparison-product-select')) {
  let activeSelect = null;
  let scrollLockOwner = null;
  let previousBodyOverflow = '';
  let previousDocumentOverflow = '';
  const MOBILE_TRANSITION_MS = 300;

  class ComparisonProductSelect extends HTMLElement {
    static get observedAttributes() {
      return ['value'];
    }

    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.innerHTML = `
        <style>
          :host {
            --select-padding: 14px 12px;
            display: block;
            width: 100%;
            position: relative;
          }

          * {
            box-sizing: border-box;
          }

          .select-trigger {
            width: 100%;
            min-height: 50px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: var(--select-padding);
            border: 1px solid #ccc;
            border-radius: 8px;
            background: #fff;
            color: #333;
            font: inherit;
            font-weight: 400;
            line-height: 1.2;
            text-align: left;
            cursor: pointer;
            user-select: none;
          }

          .select-trigger:hover,
          .select-trigger:focus-visible {
            border-color: #333;
          }

          .select-trigger:focus-visible,
          .option-card:focus-visible,
          .close-button:focus-visible {
            outline: 2px solid #168bff;
            outline-offset: 2px;
          }

          .selected-label {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .arrow {
            width: 24px;
            height: 24px;
            flex: 0 0 24px;
            display: grid;
            place-items: center;
            transition: transform 0.2s ease;
          }

          .options-panel {
            position: fixed;
            z-index: 1000;
            display: none;
            flex-direction: column;
            overflow: hidden;
            border-radius: 8px;
            background: #fff;
            box-shadow: 0 5px 14px rgba(0, 0, 0, 0.16);
          }

          .open .options-panel {
            display: flex;
          }

          .options-grid {
            min-height: 0;
            flex: 1 1 auto;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 12px;
            padding: 18px 20px;
            overflow-y: auto;
            overscroll-behavior: contain;
          }

          .option-card {
            position: relative;
            min-width: 0;
            min-height: 210px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding: 12px 10px 16px;
            border: 1px solid #e2e2e2;
            border-radius: 9px;
            background: #fff;
            color: #111;
            font: inherit;
            cursor: pointer;
            overflow: hidden;
            transition: border-color 0.15s ease;
          }

          .option-card:hover:not(:disabled) {
            border-color: #457084;
          }

          .option-card[aria-selected='true'] {
            border: 2px solid #457084;
            background: #fff;
          }

          .option-card[aria-selected='true'] .option-label {
            font-weight: 600;
          }

          .option-card:disabled {
            border-color: #e2e2e2;
            background: #fff;
            color: #111;
            cursor: not-allowed;
          }

          .option-card:disabled::after {
            content: '';
            position: absolute;
            inset: 0;
            z-index: 2;
            border-radius: inherit;
            background: rgba(102, 102, 102, 0.25);
            pointer-events: none;
          }

          .option-image-wrap {
            width: 100%;
            height: 150px;
            min-width: 0;
            min-height: 0;
            display: grid;
            place-items: center;
            overflow: hidden;
          }

          .option-image {
            display: block;
            width: auto;
            height: auto;
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }

          .option-label {
            width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 17px;
            line-height: 1.25;
            text-align: center;
          }

          .mobile-panel-header,
          .close-button {
            display: none;
          }

          @media (max-width: 1023px) {
            .options-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (min-width: 769px) and (max-width: 1023px) {
            .option-card {
              height: 210px;
              min-height: 210px;
              display: grid;
              grid-template-columns: minmax(0, 1fr);
              grid-template-rows: minmax(0, 1fr) 22px;
              align-items: stretch;
              justify-items: stretch;
              justify-content: stretch;
            }

            .option-image-wrap {
              position: relative;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              align-self: stretch;
              justify-self: stretch;
            }

            .option-image {
              position: static;
              width: auto;
              height: 100%;
              max-width: none;
              max-height: none;
              object-fit: unset;
            }

            .option-label {
              width: 100%;
              height: 22px;
              line-height: 22px;
              text-align: center;
            }
          }

          @media (max-width: 768px) {
            :host {
              --select-padding: 12px;
            }

            .options-panel {
              inset: 0 !important;
              width: 100vw !important;
              max-width: none !important;
              height: 100vh;
              height: 100dvh;
              max-height: none !important;
              display: flex;
              border-radius: 0;
              box-shadow: none;
              z-index: 10000;
              opacity: 0;
              visibility: hidden;
              pointer-events: none;
              transform: translateY(100%);
              transition:
                transform 300ms ease,
                opacity 300ms ease,
                visibility 0s linear 300ms;
              will-change: transform, opacity;
            }

            .open .options-panel {
              opacity: 1;
              visibility: visible;
              pointer-events: auto;
              transform: translateY(0);
              transition-delay: 0s;
            }

            .mobile-panel-header {
              flex: 0 0 55px;
              display: flex;
              align-items: stretch;
              padding: 0 16px;
              background: #fff;
            }

            .mobile-current {
              width: min(165px, 48vw);
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              padding: 0 16px;
              border-bottom: 1px solid #168bff;
              color: #222;
              font-size: 16px;
            }

            .mobile-current-label {
              min-width: 0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .mobile-current .arrow {
              color: #999;
            }

            .options-grid {
              flex: 1 1 auto;
              align-content: start;
              gap: 12px;
              padding: 30px 16px calc(92px + env(safe-area-inset-bottom));
            }

            .option-card {
              height: 188px;
              min-height: 188px;
              display: grid;
              grid-template-columns: minmax(0, 1fr);
              grid-template-rows: minmax(0, 1fr) 24px;
              align-items: stretch;
              justify-items: stretch;
              justify-content: stretch;
              padding: 10px 8px 15px;
            }

            .option-image-wrap {
              position: relative;
              width: 100%;
              height: 100%;
              min-height: 0;
              display: grid;
              place-items: center;
              overflow: hidden;
              align-self: stretch;
              justify-self: stretch;
            }

            .option-image {
              position: absolute;
              inset: 0;
              width: 100%;
              height: 100%;
              max-width: none;
              max-height: none;
              object-fit: contain;
            }

            .option-label {
              width: 100%;
              height: 24px;
              line-height: 24px;
              font-size: 18px;
              text-align: center;
            }

            .close-button {
              position: absolute;
              left: 50%;
              bottom: calc(24px + env(safe-area-inset-bottom));
              z-index: 2;
              width: 48px;
              height: 48px;
              display: grid;
              place-items: center;
              padding: 0;
              border: 0;
              border-radius: 50%;
              background: #111;
              color: #fff;
              cursor: pointer;
              transform: translateX(-50%);
            }
          }
        </style>

        <div class="select-container">
          <button
            class="select-trigger"
            type="button"
            aria-haspopup="listbox"
            aria-expanded="false"
          >
            <span class="selected-label"></span>
            <span class="arrow" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M20 8.5 12.5 16 5 8.5" stroke="currentColor" stroke-width="1.5" fill="none"/>
              </svg>
            </span>
          </button>

          <div class="options-panel" role="listbox" aria-hidden="true">
            <div class="mobile-panel-header">
              <div class="mobile-current">
                <span class="mobile-current-label"></span>
                <span class="arrow" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path d="M20 8.5 12.5 16 5 8.5" stroke="currentColor" stroke-width="1.5" fill="none"/>
                  </svg>
                </span>
              </div>
            </div>
            <div class="options-grid"></div>
            <button class="close-button" type="button" aria-label="Close product selector">
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 5l14 14M19 5 5 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      `;

      this.container = this.shadowRoot.querySelector('.select-container');
      this.trigger = this.shadowRoot.querySelector('.select-trigger');
      this.label = this.shadowRoot.querySelector('.selected-label');
      this.mobileLabel = this.shadowRoot.querySelector('.mobile-current-label');
      this.panel = this.shadowRoot.querySelector('.options-panel');
      this.grid = this.shadowRoot.querySelector('.options-grid');
      this.closeButton = this.shadowRoot.querySelector('.close-button');
      this._isOpen = false;
      this._connected = false;
      this._observer = null;
      this._positionFrame = null;
      this._closeTimer = null;

      this._handleDocumentPointerDown = this._handleDocumentPointerDown.bind(this);
      this._handleWindowChange = this._handleWindowChange.bind(this);
      this._handleKeyDown = this._handleKeyDown.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      const listboxId = `${this.id || 'comparison-product-select'}-listbox`;
      this.panel.id = listboxId;
      this.trigger.setAttribute('aria-controls', listboxId);

      this.trigger.addEventListener('click', () => {
        this._isOpen ? this.close() : this.open();
      });
      this.trigger.addEventListener('keydown', this._handleKeyDown);
      this.grid.addEventListener('keydown', this._handleKeyDown);
      this.closeButton.addEventListener('click', () => this.close({ returnFocus: true }));
      document.addEventListener('pointerdown', this._handleDocumentPointerDown);
      window.addEventListener('resize', this._handleWindowChange);
      window.addEventListener('scroll', this._handleWindowChange);

      this._observer = new MutationObserver(() => {
        this._ensureValidValue({ emit: true });
        this._renderOptions();
        this._syncSelectedDisplay();
      });
      this._observer.observe(this, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'data-image-src', 'data-image-alt']
      });

      this._ensureValidValue({ emit: true });
      this._syncSelectedDisplay();
      this._renderOptions();
    }

    disconnectedCallback() {
      if (this._isOpen) this.close();
      this._completePendingClose();
      document.removeEventListener('pointerdown', this._handleDocumentPointerDown);
      window.removeEventListener('resize', this._handleWindowChange);
      window.removeEventListener('scroll', this._handleWindowChange);
      if (this._observer) this._observer.disconnect();
      if (this._positionFrame) cancelAnimationFrame(this._positionFrame);
      this._observer = null;
      this._connected = false;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'value' || oldValue === newValue || !this.shadowRoot) return;
      this._syncSelectedDisplay();
      this._renderOptions();
    }

    get value() {
      return this.getAttribute('value') || '';
    }

    set value(value) {
      this._applyValue(value, { emit: true });
    }

    open() {
      if (this._isOpen) return;

      if (this._closeTimer) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }

      if (activeSelect && activeSelect !== this) {
        activeSelect.close();
      }
      activeSelect = this;
      this._isOpen = true;
      this.container.classList.add('open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.panel.setAttribute('aria-hidden', 'false');
      this._renderOptions();
      this._syncSelectedDisplay();

      if (this._isFullscreen()) {
        this._lockPageScroll();
      } else {
        this._positionPanel();
      }

      requestAnimationFrame(() => this._scrollSelectedCardIntoView());
    }

    close(options = {}) {
      if (!this._isOpen) return;

      const fullscreen = this._isFullscreen();
      this._isOpen = false;
      this.container.classList.remove('open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      if (activeSelect === this) activeSelect = null;

      if (fullscreen) {
        this._closeTimer = setTimeout(() => {
          this._closeTimer = null;
          this._unlockPageScroll();
          if (options.returnFocus) this.trigger.focus({ preventScroll: true });
        }, MOBILE_TRANSITION_MS);
      } else {
        this._completePendingClose();
        if (options.returnFocus) this.trigger.focus({ preventScroll: true });
      }
    }

    _completePendingClose() {
      if (this._closeTimer) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }
      this._unlockPageScroll();
    }

    _getOptions() {
      return Array.from(this.querySelectorAll('option'));
    }

    _isOptionDisabled(option) {
      return option.disabled || option.hasAttribute('disabled');
    }

    _getFirstEnabledOption() {
      return this._getOptions().find((option) => option.value && !this._isOptionDisabled(option));
    }

    _getSelectedOption() {
      const value = this.value;
      return this._getOptions().find((option) => option.value === value) || null;
    }

    _ensureValidValue({ emit }) {
      const currentOption = this._getSelectedOption();
      const required = this.getAttribute('data-required') === 'true';

      if (currentOption && !this._isOptionDisabled(currentOption)) return;
      if (!required && !this.value) return;

      const fallback = this._getFirstEnabledOption();
      this._applyValue(fallback ? fallback.value : '', { emit });
    }

    _applyValue(value, options = {}) {
      const emit = options.emit !== false;
      const requestedValue = value == null ? '' : String(value);
      const required = this.getAttribute('data-required') === 'true';
      let option = this._getOptions().find((item) => item.value === requestedValue) || null;

      if (!option || this._isOptionDisabled(option)) {
        option = required ? this._getFirstEnabledOption() || null : null;
      }

      const nextValue = option ? option.value : '';
      if (nextValue) {
        this.setAttribute('value', nextValue);
      } else {
        this.removeAttribute('value');
      }

      this._getOptions().forEach((item) => {
        item.selected = item === option;
      });
      this._syncSelectedDisplay();
      this._renderOptions();

      if (emit) {
        this.dispatchEvent(new Event('change', { bubbles: true }));
        this.dispatchEvent(new CustomEvent('option-change', {
          detail: {
            value: nextValue,
            label: option ? option.textContent.trim() : ''
          },
          bubbles: true,
          composed: true
        }));
      }
    }

    _syncSelectedDisplay() {
      if (!this.label || !this.mobileLabel) return;
      const option = this._getSelectedOption();
      const text = option
        ? option.textContent.trim()
        : (this.getAttribute('placeholder') || 'Select a product');
      this.label.textContent = text;
      this.mobileLabel.textContent = text;
    }

    _renderOptions() {
      if (!this.grid) return;
      const fragment = document.createDocumentFragment();

      this._getOptions().forEach((option) => {
        const card = document.createElement('button');
        const disabled = this._isOptionDisabled(option);
        const selected = option.value === this.value;
        card.type = 'button';
        card.className = 'option-card';
        card.dataset.value = option.value;
        card.setAttribute('role', 'option');
        card.setAttribute('aria-selected', selected ? 'true' : 'false');
        card.disabled = disabled;
        if (disabled) card.setAttribute('aria-disabled', 'true');

        const imageWrap = document.createElement('span');
        imageWrap.className = 'option-image-wrap';
        if (option.dataset.imageSrc) {
          const image = document.createElement('img');
          image.className = 'option-image';
          image.src = option.dataset.imageSrc;
          image.alt = option.dataset.imageAlt || '';
          image.loading = 'lazy';
          imageWrap.appendChild(image);
        }

        const label = document.createElement('span');
        label.className = 'option-label';
        label.textContent = option.textContent.trim();
        label.title = option.textContent.trim();

        card.append(imageWrap, label);
        card.addEventListener('click', () => {
          if (disabled) return;
          this._applyValue(option.value, { emit: true });
          this.close({ returnFocus: !this._isFullscreen() });
        });
        fragment.appendChild(card);
      });

      this.grid.replaceChildren(fragment);
    }

    _isFullscreen() {
      return window.matchMedia('(max-width: 768px)').matches;
    }

    _positionPanel() {
      if (!this._isOpen || this._isFullscreen()) return;
      const triggerRect = this.trigger.getBoundingClientRect();
      const viewportPadding = 16;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isTwoColumn = viewportWidth < 1024;
      const desiredWidth = isTwoColumn
        ? Math.max(triggerRect.width * 2, 360)
        : 676;
      const width = Math.min(desiredWidth, viewportWidth - viewportPadding * 2);
      const left = Math.min(
        Math.max(triggerRect.left, viewportPadding),
        viewportWidth - width - viewportPadding
      );
      const spaceBelow = viewportHeight - triggerRect.bottom - viewportPadding;
      const spaceAbove = triggerRect.top - viewportPadding;
      const openAbove = spaceBelow < 260 && spaceAbove > spaceBelow;
      const availableHeight = Math.max(0, openAbove ? spaceAbove : spaceBelow);
      const maxHeight = Math.min(670, availableHeight);
      const top = openAbove
        ? Math.max(viewportPadding, triggerRect.top - maxHeight - 2)
        : triggerRect.bottom + 2;

      this.panel.style.width = `${width}px`;
      this.panel.style.left = `${left}px`;
      this.panel.style.top = `${top}px`;
      this.panel.style.maxHeight = `${maxHeight}px`;
    }

    _scrollSelectedCardIntoView() {
      const selectedCard = this.grid.querySelector('[aria-selected="true"]');
      if (!selectedCard) return;
      const targetTop = selectedCard.offsetTop - Math.max(0, (this.grid.clientHeight - selectedCard.offsetHeight) / 2);
      this.grid.scrollTop = Math.max(0, targetTop);
    }

    _handleDocumentPointerDown(event) {
      if (!this._isOpen || event.composedPath().includes(this)) return;
      this.close();
    }

    _handleWindowChange() {
      if (!this._isOpen) return;

      if (this._positionFrame) cancelAnimationFrame(this._positionFrame);
      this._positionFrame = requestAnimationFrame(() => {
        this._positionFrame = null;
        if (this._isFullscreen()) {
          this._lockPageScroll();
          this.panel.removeAttribute('style');
        } else {
          this._unlockPageScroll();
          this._positionPanel();
        }
      });
    }

    _handleKeyDown(event) {
      if (event.key === 'Escape' && this._isOpen) {
        event.preventDefault();
        this.close({ returnFocus: true });
        return;
      }

      if (event.currentTarget === this.trigger) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.open();
          requestAnimationFrame(() => {
            const selected = this.grid.querySelector('[aria-selected="true"]:not(:disabled)');
            const first = this.grid.querySelector('.option-card:not(:disabled)');
            const focusTarget = selected || first;
            if (focusTarget) focusTarget.focus();
          });
        }
        return;
      }

      if (!event.target.classList.contains('option-card')) return;
      const cards = Array.from(this.grid.querySelectorAll('.option-card:not(:disabled)'));
      const currentIndex = cards.indexOf(event.target);
      const columns = window.innerWidth < 1024 ? 2 : 4;
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex += 1;
      if (event.key === 'ArrowLeft') nextIndex -= 1;
      if (event.key === 'ArrowDown') nextIndex += columns;
      if (event.key === 'ArrowUp') nextIndex -= columns;
      if (nextIndex === currentIndex) return;

      event.preventDefault();
      nextIndex = Math.max(0, Math.min(cards.length - 1, nextIndex));
      if (cards[nextIndex]) cards[nextIndex].focus();
    }

    _lockPageScroll() {
      if (scrollLockOwner === this) return;
      if (scrollLockOwner && scrollLockOwner !== this) {
        scrollLockOwner._completePendingClose();
      }

      previousBodyOverflow = document.body.style.overflow;
      previousDocumentOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      scrollLockOwner = this;
    }

    _unlockPageScroll() {
      if (scrollLockOwner !== this) return;
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;
      scrollLockOwner = null;
    }
  }

  customElements.define('comparison-product-select', ComparisonProductSelect);
}
